import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Edit3,
  Eye,
  FileText,
  Globe,
  ImagePlus,
  Plus,
  Save,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import GuideContentEditor from './GuideContentEditor';
import { adminGuidesAPI } from '../services/adminApi';
import { appUrl } from '../utils/appPaths';
import { stripHtmlToText } from '../utils/seo';
import { createGuidePreviewDraft } from '../utils/guidePreviewDraft';

const PanelContainer = styled.div`
  display: grid;
  gap: 20px;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  padding-bottom: 18px;
  border-bottom: 1px solid #e5e7eb;
`;

const PanelHeadingBlock = styled.div`
  display: grid;
  gap: 8px;
`;

const PanelTitle = styled.h2`
  color: #1f2937;
  font-size: 1.7rem;
  font-weight: 700;
`;

const PanelDescription = styled.p`
  color: #6b7280;
  max-width: 72ch;
  line-height: 1.7;
`;

const PanelActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const buttonStyles = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease, background 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  ${buttonStyles}
  background: #2c9fa4;
  color: white;

  &:hover:not(:disabled) {
    background: #1c6568;
  }
`;

const SecondaryButton = styled.button`
  ${buttonStyles}
  background: #e2e8f0;
  color: #334155;

  &:hover:not(:disabled) {
    background: #cbd5e1;
  }
`;

const Card = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  padding: 20px;
`;

const SearchBar = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px 140px;
  gap: 12px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const SearchField = styled.label`
  position: relative;
  display: block;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  top: 50%;
  left: 14px;
  transform: translateY(-50%);
  color: #94a3b8;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px 12px ${props => props.$withIcon ? '42px' : '14px'};
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font: inherit;
  color: #1f2937;
  background: white;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.12);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font: inherit;
  color: #1f2937;
  background: white;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.12);
  }
`;

const ListMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  color: #64748b;
  font-size: 14px;
  margin-top: 14px;
`;

const GuidesList = styled.div`
  display: grid;
  gap: 16px;
`;

const GuideRow = styled(Card)`
  padding: 22px;
  display: grid;
  gap: 16px;
`;

const GuideRowTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
`;

const GuideRowTitleBlock = styled.div`
  display: grid;
  gap: 10px;
`;

const GuideRowTitle = styled.h3`
  color: #111827;
  font-size: 1.15rem;
  font-weight: 700;
`;

const GuideRowSlug = styled.div`
  color: #0f766e;
  font-size: 13px;
  font-weight: 600;
`;

const GuideRowExcerpt = styled.p`
  color: #475569;
  line-height: 1.7;
  font-size: 0.95rem;
`;

const GuideRowMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  border-radius: 999px;
  background: #f8fafc;
  color: #334155;
  font-size: 12px;
  font-weight: 600;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: ${props => {
    switch (props.$status) {
      case 'published':
        return '#dcfce7';
      case 'scheduled':
        return '#e0f2fe';
      case 'unpublished':
        return '#fef3c7';
      default:
        return '#e5e7eb';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'published':
        return '#166534';
      case 'scheduled':
        return '#075985';
      case 'unpublished':
        return '#92400e';
      default:
        return '#374151';
    }
  }};
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const GuideRowActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const ActionButton = styled.button`
  ${buttonStyles}
  padding: 8px 12px;
  border-radius: 8px;
  background: ${props => props.$tone === 'danger' ? '#fee2e2' : props.$tone === 'success' ? '#dcfce7' : props.$tone === 'warning' ? '#fef3c7' : '#eff6ff'};
  color: ${props => props.$tone === 'danger' ? '#b91c1c' : props.$tone === 'success' ? '#166534' : props.$tone === 'warning' ? '#92400e' : '#1d4ed8'};
`;

const EmptyState = styled(Card)`
  min-height: 220px;
  display: grid;
  place-items: center;
  text-align: center;
  color: #6b7280;
`;

const FormCard = styled(Card)`
  display: grid;
  gap: 24px;
`;

const FormSection = styled.div`
  display: grid;
  gap: 16px;
`;

const FormSectionTitle = styled.h3`
  color: #0f172a;
  font-size: 1.1rem;
  font-weight: 700;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  display: grid;
  gap: 8px;
  grid-column: ${props => props.$full ? '1 / -1' : 'auto'};
`;

const Label = styled.label`
  color: #334155;
  font-size: 14px;
  font-weight: 600;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: ${props => props.$tall ? '132px' : '96px'};
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font: inherit;
  color: #1f2937;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.12);
  }
`;

const HelperText = styled.div`
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
`;

const ModeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const ModeCard = styled.button`
  text-align: left;
  border: 1px solid ${props => props.$active ? '#2c9fa4' : '#d1d5db'};
  background: ${props => props.$active ? '#f0fdfa' : 'white'};
  color: #1f2937;
  border-radius: 14px;
  padding: 16px;
  cursor: pointer;
  display: grid;
  gap: 8px;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: #2c9fa4;
  }
`;

const ModeTitle = styled.div`
  font-weight: 700;
`;

const ModeDescription = styled.div`
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
`;

const ImagePreview = styled.div`
  display: grid;
  gap: 12px;
`;

const CoverPreview = styled.img`
  width: min(100%, 480px);
  max-height: 260px;
  object-fit: cover;
  border-radius: 16px;
  border: 1px solid #dbe4ea;
  background: #f8fafc;
`;

const InlineButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Message = styled.div`
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid ${props => props.$tone === 'error' ? '#fecaca' : '#bbf7d0'};
  background: ${props => props.$tone === 'error' ? '#fef2f2' : '#f0fdf4'};
  color: ${props => props.$tone === 'error' ? '#b91c1c' : '#166534'};
  font-size: 14px;
  line-height: 1.6;
`;

const formatGuideDate = (value) => {
  if (!value) {
    return '';
  }

  const normalizedValue = String(value).includes('T')
    ? value
    : `${String(value).replace(' ', 'T')}Z`;
  const parsedDate = new Date(normalizedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  return parsedDate.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const utcToLocalDateTimeInput = (value) => {
  if (!value) {
    return '';
  }

  const normalizedValue = String(value).includes('T')
    ? value
    : `${String(value).replace(' ', 'T')}Z`;
  const parsedDate = new Date(normalizedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  const pad = (segment) => String(segment).padStart(2, '0');

  return `${parsedDate.getFullYear()}-${pad(parsedDate.getMonth() + 1)}-${pad(parsedDate.getDate())}T${pad(parsedDate.getHours())}:${pad(parsedDate.getMinutes())}`;
};

const localDateTimeToUtcIso = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate.toISOString();
};

const buildSlug = (value) => String(value || '')
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .replace(/-{2,}/g, '-');

const buildExcerptPreview = (guide) => {
  const explicitExcerpt = stripHtmlToText(guide?.excerpt);
  if (explicitExcerpt) {
    return explicitExcerpt.length > 180
      ? `${explicitExcerpt.slice(0, 177).trimEnd()}...`
      : explicitExcerpt;
  }

  const contentExcerpt = stripHtmlToText(guide?.content_html);
  return contentExcerpt.length > 180
    ? `${contentExcerpt.slice(0, 177).trimEnd()}...`
    : contentExcerpt;
};

const inferPublicationMode = (guide) => {
  if (guide?.status === 'scheduled') {
    return 'schedule';
  }

  return guide?.published ? 'publish' : 'draft';
};

const createEmptyGuideForm = () => ({
  id: null,
  title: '',
  slug: '',
  excerpt: '',
  content_html: '',
  cover_image_url: '',
  meta_description: '',
  seo_keywords: '',
  publish_at: '',
  unpublish_at: '',
  published_at: '',
  created_at: '',
  updated_at: '',
  status: 'draft',
  published: 0,
});

const createGuideFormState = (guide) => ({
  id: guide?.id ?? null,
  title: guide?.title || '',
  slug: guide?.slug || '',
  excerpt: guide?.excerpt || '',
  content_html: guide?.content_html || '',
  cover_image_url: guide?.cover_image_url || '',
  meta_description: guide?.meta_description || '',
  seo_keywords: guide?.seo_keywords || '',
  publish_at: utcToLocalDateTimeInput(guide?.publish_at),
  unpublish_at: utcToLocalDateTimeInput(guide?.unpublish_at),
  published_at: guide?.published_at || '',
  created_at: guide?.created_at || '',
  updated_at: guide?.updated_at || '',
  status: guide?.status || 'draft',
  published: guide?.published ? 1 : 0,
});

const GuidesAdminPanel = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const coverUploadInputRef = useRef(null);

  const [guides, setGuides] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [publicationMode, setPublicationMode] = useState('draft');
  const [guideForm, setGuideForm] = useState(createEmptyGuideForm);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingGuide, setLoadingGuide] = useState(false);
  const [savingGuide, setSavingGuide] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [message, setMessage] = useState({ tone: '', text: '' });
  const [operationKey, setOperationKey] = useState('');

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const editArticleId = searchParams.get('editArticle') || '';

  const setFeedback = (tone, text) => {
    setMessage({ tone, text });
  };

  const resetToList = () => {
    setViewMode('list');
    setPublicationMode('draft');
    setGuideForm(createEmptyGuideForm());
    setOperationKey('');
    setFeedback('', '');
    navigate({
      pathname: location.pathname,
      search: '?tab=guides',
    }, { replace: true });
  };

  const fetchGuides = useCallback(async () => {
    try {
      setLoadingList(true);
      const response = await adminGuidesAPI.getAll();
      setGuides(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load guides:', error);
      setFeedback('error', 'Failed to load guides.');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchGuides();
  }, [fetchGuides]);

  const syncGuideInList = (savedGuide) => {
    setGuides((currentGuides) => {
      const nextGuides = Array.isArray(currentGuides) ? [...currentGuides] : [];
      const existingIndex = nextGuides.findIndex((guide) => String(guide.id) === String(savedGuide.id));

      if (existingIndex >= 0) {
        nextGuides[existingIndex] = savedGuide;
      } else {
        nextGuides.unshift(savedGuide);
      }

      return nextGuides.sort((leftGuide, rightGuide) => new Date(rightGuide.updated_at || rightGuide.created_at || 0) - new Date(leftGuide.updated_at || leftGuide.created_at || 0));
    });
  };

  const removeGuideFromList = (guideId) => {
    setGuides((currentGuides) => currentGuides.filter((guide) => String(guide.id) !== String(guideId)));
  };

  const loadGuideForEdit = useCallback(async (guideId, updateUrl = true) => {
    try {
      if (updateUrl) {
        navigate({
          pathname: location.pathname,
          search: `?tab=guides&editArticle=${guideId}`,
        }, { replace: true });
      }

      setLoadingGuide(true);
      setFeedback('', '');
      const response = await adminGuidesAPI.getById(guideId);
      const loadedGuide = response.data;
      setGuideForm(createGuideFormState(loadedGuide));
      setPublicationMode(inferPublicationMode(loadedGuide));
      setViewMode('edit');
    } catch (error) {
      console.error('Failed to load guide:', error);
      setFeedback('error', 'Failed to load the selected guide.');
    } finally {
      setLoadingGuide(false);
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (editArticleId && String(guideForm.id || '') !== String(editArticleId)) {
      loadGuideForEdit(editArticleId, false);
    }
  }, [editArticleId, guideForm.id, loadGuideForEdit]);

  const filteredGuides = useMemo(() => {
    return guides.filter((guide) => {
      const matchesSearch = !searchTerm || [
        guide.title,
        guide.slug,
        guide.excerpt,
      ].some((value) => String(value || '').toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus = statusFilter === 'all' || guide.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [guides, searchTerm, statusFilter]);

  const handleCreateNew = () => {
    setGuideForm(createEmptyGuideForm());
    setPublicationMode('draft');
    setViewMode('create');
    setFeedback('', '');
    navigate({
      pathname: location.pathname,
      search: '?tab=guides',
    }, { replace: true });
  };

  const handleGuideFieldChange = (field, value) => {
    setGuideForm((currentForm) => {
      const nextForm = {
        ...currentForm,
        [field]: value,
      };

      if (field === 'title' && !currentForm.slug) {
        nextForm.slug = buildSlug(value);
      }

      return nextForm;
    });
  };

  const handleRegenerateSlug = () => {
    setGuideForm((currentForm) => ({
      ...currentForm,
      slug: buildSlug(currentForm.title),
    }));
  };

  const uploadGuideImage = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await adminGuidesAPI.uploadImage(formData);
    return response.data?.url || '';
  }, []);

  const handleCoverUpload = async (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    try {
      setUploadingCover(true);
      const imageUrl = await uploadGuideImage(selectedFile);
      handleGuideFieldChange('cover_image_url', imageUrl);
      setFeedback('success', 'Cover image uploaded successfully.');
    } catch (error) {
      console.error('Failed to upload cover image:', error);
      setFeedback('error', 'Failed to upload cover image.');
    } finally {
      setUploadingCover(false);
      event.target.value = '';
    }
  };

  const buildSavePayload = () => {
    const payload = {
      title: guideForm.title,
      slug: guideForm.slug,
      excerpt: guideForm.excerpt,
      content_html: guideForm.content_html,
      cover_image_url: guideForm.cover_image_url,
      meta_description: guideForm.meta_description,
      seo_keywords: guideForm.seo_keywords,
    };

    if (publicationMode === 'draft') {
      payload.published = false;
      payload.publish_at = null;
      payload.unpublish_at = null;
      return payload;
    }

    if (publicationMode === 'publish') {
      payload.published = true;
      payload.publish_at = null;
      payload.unpublish_at = localDateTimeToUtcIso(guideForm.unpublish_at);
      return payload;
    }

    if (!guideForm.publish_at) {
      throw new Error('Choose a publish time to schedule this guide.');
    }

    payload.published = false;
    payload.publish_at = localDateTimeToUtcIso(guideForm.publish_at);
    payload.unpublish_at = localDateTimeToUtcIso(guideForm.unpublish_at);
    return payload;
  };

  const handleSaveGuide = async () => {
    try {
      setSavingGuide(true);
      setFeedback('', '');

      const payload = buildSavePayload();
      const response = guideForm.id
        ? await adminGuidesAPI.update(guideForm.id, payload)
        : await adminGuidesAPI.create(payload);
      const savedGuide = response.data;

      syncGuideInList(savedGuide);
      setGuideForm(createGuideFormState(savedGuide));
      setPublicationMode(inferPublicationMode(savedGuide));
      setViewMode('edit');
      setFeedback('success', guideForm.id ? 'Guide updated successfully.' : 'Guide created successfully.');

      navigate({
        pathname: location.pathname,
        search: `?tab=guides&editArticle=${savedGuide.id}`,
      }, { replace: true });
    } catch (error) {
      console.error('Failed to save guide:', error);
      setFeedback('error', error?.response?.data?.error || error?.message || 'Failed to save guide.');
    } finally {
      setSavingGuide(false);
    }
  };

  const handleDeleteGuide = async (guide) => {
    if (!window.confirm(`Delete "${guide.title}"? This will also remove any uploaded guide media no longer referenced by the article.`)) {
      return;
    }

    try {
      const nextOperationKey = `delete-${guide.id}`;
      setOperationKey(nextOperationKey);
      setFeedback('', '');
      await adminGuidesAPI.delete(guide.id);
      removeGuideFromList(guide.id);
      setFeedback('success', 'Guide deleted successfully.');

      if (String(guideForm.id || '') === String(guide.id)) {
        resetToList();
      }
    } catch (error) {
      console.error('Failed to delete guide:', error);
      setFeedback('error', 'Failed to delete guide.');
    } finally {
      setOperationKey('');
    }
  };

  const handleQuickPublishToggle = async (guide, shouldPublish) => {
    try {
      const nextOperationKey = `publish-${guide.id}`;
      setOperationKey(nextOperationKey);
      setFeedback('', '');
      const response = await adminGuidesAPI.update(guide.id, {
        published: shouldPublish,
        publish_at: null,
        unpublish_at: null,
      });
      syncGuideInList(response.data);
      setFeedback('success', shouldPublish ? 'Guide published.' : 'Guide moved back to draft.');
    } catch (error) {
      console.error('Failed to update guide publication state:', error);
      setFeedback('error', 'Failed to update guide publication state.');
    } finally {
      setOperationKey('');
    }
  };

  const openPreview = (guideId, draftGuide = null) => {
    let previewUrl = appUrl(`/guides/preview/${guideId}`);

    if (guideId && draftGuide) {
      const previewToken = createGuidePreviewDraft(guideId, draftGuide);

      if (previewToken) {
        previewUrl = `${previewUrl}?previewToken=${encodeURIComponent(previewToken)}`;
      }
    }

    window.open(previewUrl, '_blank', 'noopener,noreferrer');
  };

  const openPublicGuide = (slug) => {
    window.open(appUrl(`/guides/${slug}`), '_blank', 'noopener,noreferrer');
  };

  const currentGuideSummary = useMemo(() => {
    if (!guideForm.title) {
      return '';
    }

    return buildExcerptPreview(guideForm);
  }, [guideForm]);

  return (
    <PanelContainer>
      <PanelHeader>
        <PanelHeadingBlock>
          <PanelTitle>Guides</PanelTitle>
          <PanelDescription>
            Publish blog-style public articles with rich formatting, inline image uploads, SEO fields, and scheduled publishing or unpublishing.
          </PanelDescription>
        </PanelHeadingBlock>

        <PanelActions>
          {viewMode !== 'list' && (
            <SecondaryButton type="button" onClick={resetToList}>
              <X size={16} />
              Back to List
            </SecondaryButton>
          )}
          <PrimaryButton type="button" onClick={handleCreateNew}>
            <Plus size={16} />
            New Guide
          </PrimaryButton>
        </PanelActions>
      </PanelHeader>

      {message.text && (
        <Message $tone={message.tone === 'error' ? 'error' : 'success'}>
          {message.text}
        </Message>
      )}

      {viewMode === 'list' ? (
        <>
          <Card>
            <SearchBar>
              <SearchField>
                <SearchIcon size={16} />
                <Input
                  $withIcon
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search guide titles, slugs, or summaries..."
                />
              </SearchField>

              <Select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="all">All statuses</option>
                <option value="published">Published</option>
                <option value="scheduled">Scheduled</option>
                <option value="draft">Draft</option>
                <option value="unpublished">Unpublished</option>
              </Select>

              <SecondaryButton type="button" onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}>
                Clear Filters
              </SecondaryButton>
            </SearchBar>

            <ListMeta>
              <span>{`${filteredGuides.length} guide${filteredGuides.length === 1 ? '' : 's'} shown`}</span>
              <span>The admin Guides home stays in list view so drafts, scheduled posts, and published articles are all easy to scan.</span>
            </ListMeta>
          </Card>

          {loadingList ? (
            <Card>Loading guides...</Card>
          ) : filteredGuides.length === 0 ? (
            <EmptyState>
              <div>
                <FileText size={40} style={{ color: '#94a3b8', marginBottom: '12px' }} />
                <h3 style={{ marginBottom: '8px', color: '#334155' }}>No guides found</h3>
                <p>Create your first guide or adjust the current filters.</p>
              </div>
            </EmptyState>
          ) : (
            <GuidesList>
              {filteredGuides.map((guide) => (
                <GuideRow key={guide.id}>
                  <GuideRowTop>
                    <GuideRowTitleBlock>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                        <GuideRowTitle>{guide.title}</GuideRowTitle>
                        <StatusBadge $status={guide.status}>{guide.status}</StatusBadge>
                      </div>
                      <GuideRowSlug>{`/guides/${guide.slug}`}</GuideRowSlug>
                    </GuideRowTitleBlock>

                    <GuideRowActions>
                      <ActionButton
                        type="button"
                        onClick={() => navigate({
                          pathname: location.pathname,
                          search: `?tab=guides&editArticle=${guide.id}`,
                        })}
                      >
                        <Edit3 size={15} />
                        Edit
                      </ActionButton>
                      <ActionButton type="button" onClick={() => openPreview(guide.id)}>
                        <Eye size={15} />
                        Preview
                      </ActionButton>
                      {guide.published && (
                        <ActionButton type="button" onClick={() => openPublicGuide(guide.slug)}>
                          <Globe size={15} />
                          View
                        </ActionButton>
                      )}
                      <ActionButton
                        type="button"
                        $tone={guide.published ? 'warning' : 'success'}
                        disabled={operationKey === `publish-${guide.id}`}
                        onClick={() => handleQuickPublishToggle(guide, !guide.published)}
                      >
                        {guide.published ? 'Unpublish' : 'Publish Now'}
                      </ActionButton>
                      <ActionButton
                        type="button"
                        $tone="danger"
                        disabled={operationKey === `delete-${guide.id}`}
                        onClick={() => handleDeleteGuide(guide)}
                      >
                        <Trash2 size={15} />
                        Delete
                      </ActionButton>
                    </GuideRowActions>
                  </GuideRowTop>

                  {buildExcerptPreview(guide) && (
                    <GuideRowExcerpt>{buildExcerptPreview(guide)}</GuideRowExcerpt>
                  )}

                  <GuideRowMeta>
                    <MetaPill>{`Updated ${formatGuideDate(guide.updated_at || guide.created_at)}`}</MetaPill>
                    {guide.published_at && <MetaPill>{`Published ${formatGuideDate(guide.published_at)}`}</MetaPill>}
                    {guide.publish_at && guide.status === 'scheduled' && <MetaPill>{`Scheduled ${formatGuideDate(guide.publish_at)}`}</MetaPill>}
                    {guide.unpublish_at && <MetaPill>{`Unpublish ${formatGuideDate(guide.unpublish_at)}`}</MetaPill>}
                  </GuideRowMeta>
                </GuideRow>
              ))}
            </GuidesList>
          )}
        </>
      ) : (
        <FormCard>
          {loadingGuide ? (
            <div>Loading guide editor...</div>
          ) : (
            <>
              <FormSection>
                <FormSectionTitle>{viewMode === 'create' ? 'Create Guide' : 'Edit Guide'}</FormSectionTitle>
                <HelperText>
                  Use rich formatting, upload article images directly into the editor, then save as a draft, publish now, or schedule the article.
                </HelperText>
              </FormSection>

              <FormSection>
                <FormGrid>
                  <FieldGroup>
                    <Label htmlFor="guide-title">Title</Label>
                    <Input
                      id="guide-title"
                      type="text"
                      value={guideForm.title}
                      onChange={(event) => handleGuideFieldChange('title', event.target.value)}
                      placeholder="e.g. Wiring a BME280 Over I2C"
                    />
                  </FieldGroup>

                  <FieldGroup>
                    <Label htmlFor="guide-slug">Slug</Label>
                    <Input
                      id="guide-slug"
                      type="text"
                      value={guideForm.slug}
                      onChange={(event) => handleGuideFieldChange('slug', buildSlug(event.target.value))}
                      placeholder="wiring-a-bme280-over-i2c"
                    />
                    <InlineButtonRow>
                      <SecondaryButton type="button" onClick={handleRegenerateSlug}>
                        Regenerate Slug
                      </SecondaryButton>
                    </InlineButtonRow>
                  </FieldGroup>

                  <FieldGroup $full>
                    <Label htmlFor="guide-excerpt">Summary / Excerpt</Label>
                    <TextArea
                      id="guide-excerpt"
                      value={guideForm.excerpt}
                      onChange={(event) => handleGuideFieldChange('excerpt', event.target.value)}
                      placeholder="Short summary shown on the public guides list..."
                    />
                    <HelperText>
                      If you leave this blank, MicroPinouts will derive a summary from the article body.
                    </HelperText>
                  </FieldGroup>
                </FormGrid>
              </FormSection>

              <FormSection>
                <FormSectionTitle>Cover Image</FormSectionTitle>
                <ImagePreview>
                  {guideForm.cover_image_url ? (
                    <CoverPreview src={guideForm.cover_image_url} alt={guideForm.title || 'Guide cover'} />
                  ) : (
                    <HelperText>No cover image uploaded yet. This is optional, but it helps the public Guides grid feel more polished.</HelperText>
                  )}

                  <InlineButtonRow>
                    <PrimaryButton
                      type="button"
                      onClick={() => coverUploadInputRef.current?.click()}
                      disabled={uploadingCover}
                    >
                      {uploadingCover ? <Upload size={16} /> : <ImagePlus size={16} />}
                      {uploadingCover ? 'Uploading...' : 'Upload Cover'}
                    </PrimaryButton>
                    {guideForm.cover_image_url && (
                      <SecondaryButton type="button" onClick={() => handleGuideFieldChange('cover_image_url', '')}>
                        Remove Cover
                      </SecondaryButton>
                    )}
                  </InlineButtonRow>

                  <input
                    ref={coverUploadInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={handleCoverUpload}
                    style={{ display: 'none' }}
                  />
                </ImagePreview>
              </FormSection>

              <FormSection>
                <FormSectionTitle>Article Body</FormSectionTitle>
                <HelperText>
                  Switch between HTML-backed rich text and markdown while editing. Both modes can upload article images into the Guides media library and keep the saved guide body in HTML for publishing.
                </HelperText>
                <GuideContentEditor
                  htmlValue={guideForm.content_html}
                  onChange={(value) => handleGuideFieldChange('content_html', value)}
                  height={320}
                  onImageUpload={uploadGuideImage}
                  disabled={savingGuide}
                />
                {currentGuideSummary && (
                  <HelperText>{`Current article preview: ${currentGuideSummary}`}</HelperText>
                )}
              </FormSection>

              <FormSection>
                <FormSectionTitle>Publication</FormSectionTitle>
                <ModeGrid>
                  <ModeCard type="button" $active={publicationMode === 'draft'} onClick={() => setPublicationMode('draft')}>
                    <ModeTitle>Draft</ModeTitle>
                    <ModeDescription>Keep the guide private in admin. Use this for works in progress and content review.</ModeDescription>
                  </ModeCard>
                  <ModeCard type="button" $active={publicationMode === 'publish'} onClick={() => setPublicationMode('publish')}>
                    <ModeTitle>Publish Now</ModeTitle>
                    <ModeDescription>Make the guide live immediately. You can still set an optional auto-unpublish time below.</ModeDescription>
                  </ModeCard>
                  <ModeCard type="button" $active={publicationMode === 'schedule'} onClick={() => setPublicationMode('schedule')}>
                    <ModeTitle>Schedule</ModeTitle>
                    <ModeDescription>Keep the guide private until an exact publish time, then optionally unpublish it later.</ModeDescription>
                  </ModeCard>
                </ModeGrid>

                <FormGrid>
                  {publicationMode === 'schedule' && (
                    <FieldGroup>
                      <Label htmlFor="guide-publish-at">Publish Time</Label>
                      <Input
                        id="guide-publish-at"
                        type="datetime-local"
                        value={guideForm.publish_at}
                        onChange={(event) => handleGuideFieldChange('publish_at', event.target.value)}
                      />
                      <HelperText>Scheduled times use your current browser timezone and are stored in UTC.</HelperText>
                    </FieldGroup>
                  )}

                  {publicationMode !== 'draft' && (
                    <FieldGroup>
                      <Label htmlFor="guide-unpublish-at">{publicationMode === 'schedule' ? 'Optional Unpublish Time' : 'Optional Auto-Unpublish Time'}</Label>
                      <Input
                        id="guide-unpublish-at"
                        type="datetime-local"
                        value={guideForm.unpublish_at}
                        onChange={(event) => handleGuideFieldChange('unpublish_at', event.target.value)}
                      />
                      <HelperText>Leave blank if the guide should stay published until you unpublish it manually.</HelperText>
                    </FieldGroup>
                  )}
                </FormGrid>
              </FormSection>

              <FormSection>
                <FormSectionTitle>SEO</FormSectionTitle>
                <FormGrid>
                  <FieldGroup $full>
                    <Label htmlFor="guide-meta-description">Meta Description</Label>
                    <TextArea
                      id="guide-meta-description"
                      value={guideForm.meta_description}
                      onChange={(event) => handleGuideFieldChange('meta_description', event.target.value)}
                      placeholder="Short search-result description for this guide..."
                    />
                  </FieldGroup>

                  <FieldGroup $full>
                    <Label htmlFor="guide-seo-keywords">SEO Keywords</Label>
                    <Input
                      id="guide-seo-keywords"
                      type="text"
                      value={guideForm.seo_keywords}
                      onChange={(event) => handleGuideFieldChange('seo_keywords', event.target.value)}
                      placeholder="bme280, esp32, i2c, sensor tutorial"
                    />
                  </FieldGroup>
                </FormGrid>
              </FormSection>

              <FormSection>
                <FormSectionTitle>Save and Preview</FormSectionTitle>
                <InlineButtonRow>
                  <PrimaryButton type="button" onClick={handleSaveGuide} disabled={savingGuide || uploadingCover}>
                    <Save size={16} />
                    {savingGuide ? 'Saving...' : guideForm.id ? 'Save Guide' : 'Create Guide'}
                  </PrimaryButton>

                  {guideForm.id && (
                    <SecondaryButton type="button" onClick={() => openPreview(guideForm.id, guideForm)}>
                      <Eye size={16} />
                      Preview Draft
                    </SecondaryButton>
                  )}

                  {guideForm.id && guideForm.slug && guideForm.published ? (
                    <SecondaryButton type="button" onClick={() => openPublicGuide(guideForm.slug)}>
                      <Globe size={16} />
                      View Public
                    </SecondaryButton>
                  ) : null}
                </InlineButtonRow>

                {(guideForm.published_at || guideForm.updated_at) && (
                  <HelperText>
                    {guideForm.published_at ? `Published ${formatGuideDate(guideForm.published_at)}.` : 'Not yet published.'}
                    {guideForm.updated_at ? ` Last updated ${formatGuideDate(guideForm.updated_at)}.` : ''}
                  </HelperText>
                )}
              </FormSection>
            </>
          )}
        </FormCard>
      )}
    </PanelContainer>
  );
};

export default GuidesAdminPanel;
